import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import RootLayout from "./mvp/navigation/RootLayout";
import { AnalysisLayout } from "./mvp/navigation/AnalysisLayout";
import HomeScreen from "./mvp/screens/HomeScreen";
import UploadScreen from "./mvp/screens/UploadScreen";
import PlanScreen from "./mvp/screens/PlanScreen";
import AnalysisSummaryScreen from "./mvp/screens/AnalysisSummaryScreen";
import AnalysisDetailsScreen from "./mvp/screens/AnalysisDetailsScreen";
import ProfileScreen from "./mvp/screens/ProfileScreen";
import { BottomNav } from "@/components/ui/bottom-nav";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { PaymentGateProvider } from "@/hooks/usePaymentGate";
import Auth from "./pages/Auth";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Home from "./pages/Home";
import HomeV2 from "./pages/HomeV2";
import LandingPage from "./components/marketing/LandingPage";
import Record from "./pages/Record";
import Capture from "./pages/Capture";
import Library from "./pages/Library";
import Analyze from "./pages/Analyze";
import Practice from "./pages/Practice";
import Profile from "./pages/Profile";
import TestPose from "./pages/TestPose";
import PoseAnalysisViewer from "./pages/PoseAnalysisViewer";
import Video from "./pages/Video";
import Videos from "./pages/Videos";
import VideoAnalysis from "./pages/VideoAnalysis";
import AnalysisPage from "./pages/AnalysisPage";
import AnalysisProgressPage from "./pages/AnalysisProgressPage";
import RoastPage from "./pages/RoastPage";
import AuraPreview from "./pages/design/AuraPreview";
import { SwingLabUploadPage } from "./features/swing-lab/pages/UploadPage";
import { SwingLabAnalysisPage } from "./features/swing-lab/pages/AnalysisPage";
import { SwingLabDesignMockPage } from "./features/swing-lab/pages/DesignMockPage";
import { RunHistoryPage } from "./features/swing-lab/pages/RunHistoryPage";
import NotFound from "./pages/NotFound";
import { LibraryRedirect } from "./components/routing/LibraryRedirect";
import SystemHealthDashboard from "./pages/admin/health/SystemHealthDashboard";
import OperationsDashboard from "./pages/admin/ops/OperationsDashboard";
import TelemetryDebugPage from "./pages/admin/TelemetryDebug";
import VideoDebugPage from "./pages/admin/VideoDebug";
import CourseClaimsPage from "./pages/admin/CourseClaims";
// Archived - CourseAdminDashboard depends on archived useCourseDetail
// import { CourseAdminDashboard } from "./pages/admin/courses/[courseId]/CourseAdminDashboard";
import DiagnosticPage from "./pages/DiagnosticPage";
// Coach Dubs removed for MVP focus
import CoachDashboard from "./pages/CoachDashboard";
import CoachDirectoryPage from "./pages/CoachDirectoryPage";
import CoachFeedPage from "./pages/CoachFeedPage";
import GolferDashboard from "./pages/GolferDashboard";
import BetaMetricsPage from "./pages/beta/metrics";
import { DashboardPage } from "./pages/DashboardPage";
import LearningAdminPage from "./pages/admin/learning/LearningAdminPage";
import DebugTools from "./pages/admin/debug/DebugTools";
import AdminHub from "./pages/admin/AdminHub";
import LearningStagesAdmin from "./pages/admin/LearningStages";
import BadgeAdminPage from "./pages/admin/badges/BadgeAdminPage";
import PipelineMonitor from "./pages/admin/PipelineMonitor";
import CoachingMonitor from "./pages/admin/CoachingMonitor";
import UsersPage from "./pages/admin/UsersPage";
import AnalysisQualityDashboard from "./pages/admin/analysis-quality/AnalysisQualityDashboard";
import { ProtectedAdminRoute } from "./components/admin/ProtectedAdminRoute";
import { isUIV2Enabled, isMvpNavEnabled } from "./lib/flags";
import { OnboardingGate } from "./components/onboarding/OnboardingGate";
import SnapshotSelectSwings from "./pages/SnapshotSelectSwings";
import SnapshotResults from "./pages/SnapshotResults";
import DrillsLibraryPage from "./pages/DrillsLibraryPage";
import LibraryDrillDetailPage from "./pages/drills/LibraryDrillDetailPage";
import LibraryStartDrillPage from "./pages/drills/LibraryStartDrillPage";
import LibraryDrillExecutionPage from "./pages/drills/LibraryDrillExecutionPage";
import StartDrillPage from "./pages/training-program/StartDrillPage";
import DrillExecutionPage from "./pages/training-program/DrillExecutionPage";
import TrainingProgramOverviewPage from "./pages/training-program/TrainingProgramOverviewPage";
import DrillDetailPage from "./pages/training-program/DrillDetailPage";
import AddOptionalDrillPage from "./pages/training-program/AddOptionalDrillPage";
import ScoreExplanation from "./pages/score-explanation/[id]";
// Demo Mode (EPIC-DM01)
import DemoLanding from "./pages/demo/DemoLanding";
import DemoIntake from "./pages/demo/DemoIntake";
import DemoCapture from "./pages/demo/DemoCapture";
// DemoSelect removed — single-swing flow now handles selection inline in DemoCapture
import DemoProcessing from "./pages/demo/DemoProcessing";
import DemoResults from "./pages/demo/DemoResults";
import DemoEmailSent from "./pages/demo/DemoEmailSent";
import DemoActivation from "./pages/activate/DemoActivation";
// DM01-G: Social Share
import DemoShare from "./pages/demo/DemoShare";
import SharedSwing from "./pages/public/SharedSwing";
// Legal Pages
import TermsOfService from "./pages/legal/TermsOfService";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import SubscriptionAddendum from "./pages/legal/SubscriptionAddendum";
import AiDisclosure from "./pages/legal/AiDisclosure";
import ParentalConsent from "./pages/legal/ParentalConsent";

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

function QuickPracticeRedirect() {
  const { drillId } = useParams<{ drillId?: string }>();
  const target = drillId ? `/practice/start/${drillId}` : "/training-program";
  return <Navigate to={target} replace />;
}

function DemoSelectRedirect() {
  const { sessionId } = useParams<{ sessionId: string }>();
  return <Navigate to={`/demo/capture/${sessionId}`} replace />;
}

function AppRoutes() {
  const mvpNavEnabled = isMvpNavEnabled();
  
  return (
    <BrowserRouter>
      <div className="relative">
        <Routes>
          {/* Landing page at root - must be first */}
          <Route 
            path="/" 
            element={<LandingPage />} 
          />
          {mvpNavEnabled ? (
            <>
              <Route path="/mvp" element={<ProtectedRoute><RootLayout /></ProtectedRoute>}>
                <Route path="home" element={<HomeScreen />} />
                <Route path="upload" element={<UploadScreen />} />
                <Route path="plan" element={<PlanScreen />} />
              </Route>
              <Route path="/mvp/analysis" element={<ProtectedRoute><AnalysisSummaryScreen /></ProtectedRoute>} />
              <Route path="/mvp/analysis/:analysisId" element={<ProtectedRoute><AnalysisDetailsScreen /></ProtectedRoute>} />
              <Route path="/mvp/profile" element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>} />
            </>
          ) : null}
          {/* Legal pages (public, no auth required) */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms/subscription" element={<SubscriptionAddendum />} />
          <Route path="/terms/ai-disclosure" element={<AiDisclosure />} />
          <Route path="/terms/parental-consent" element={<ParentalConsent />} />
          {/* Auth pages */}
          <Route 
            path="/auth" 
            element={<AuthGuard><Auth /></AuthGuard>} 
          />
          <Route path="/forgot-password" element={<AuthGuard><ForgotPassword /></AuthGuard>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {mvpNavEnabled ? (
            <Route 
              path="/mvp/home" 
              element={
                <ProtectedRoute>
                  <HomeScreen />
                </ProtectedRoute>
              } 
            />
          ) : (
          <Route 
            path="/home" 
            element={
              <ProtectedRoute>
                {isUIV2Enabled() ? <HomeV2 /> : <Home />}
              </ProtectedRoute>
            } 
          />
          )}
          <Route 
            path="/record" 
            element={
              <ProtectedRoute>
                <Capture />
              </ProtectedRoute>
            } 
          />
          {/* S-3065: Redirect /library to /videos (preserve query params) */}
          <Route 
            path="/library/*" 
            element={<LibraryRedirect />} 
          />
          <Route 
            path="/videos" 
            element={
              <ProtectedRoute>
                <Videos />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/analyze" 
            element={
              <ProtectedRoute>
                <Analyze />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/practice" 
            element={
              <ProtectedRoute>
                <Practice />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/snapshot/select" 
            element={
              <ProtectedRoute>
                <SnapshotSelectSwings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/snapshot/results/:id" 
            element={
              <ProtectedRoute>
                <SnapshotResults />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/score-explanation/snapshot/:id" 
            element={
              <ProtectedRoute>
                <ScoreExplanation />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/score-explanation/swing/:id" 
            element={
              <ProtectedRoute>
                <ScoreExplanation />
              </ProtectedRoute>
            } 
          />
          {/* Drills Library */}
          <Route
            path="/drills"
            element={
              <ProtectedRoute>
                <DrillsLibraryPage />
              </ProtectedRoute>
            }
          />
          {/* UX-AUDIT-V3-100: Library drill detail route - loads any drill directly */}
          <Route
            path="/drills/:slug"
            element={
              <ProtectedRoute>
                <LibraryDrillDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drills/:slug/start"
            element={
              <ProtectedRoute>
                <LibraryStartDrillPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drills/:slug/execute"
            element={
              <ProtectedRoute>
                <LibraryDrillExecutionPage />
              </ProtectedRoute>
            }
          />
          {/* Training Program Overview */}
          <Route
            path="/training-plan/*"
            element={<Navigate to="/training-program" replace />}
          />
          <Route
            path="/training-program"
            element={
              <ProtectedRoute>
                <TrainingProgramOverviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/training-program/drill/:drillId"
            element={
              <ProtectedRoute>
                <DrillDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/training-program/optional"
            element={
              <ProtectedRoute>
                <AddOptionalDrillPage />
              </ProtectedRoute>
            }
          />
          {/* Training Program Execution Routes */}
          <Route
            path="/training-program/start/:drillId"
            element={
              <ProtectedRoute>
                <StartDrillPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/training-program/execute/:drillId"
            element={
              <ProtectedRoute>
                <DrillExecutionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/training-program/quick-practice/:drillId"
            element={
              <ProtectedRoute>
                <QuickPracticeRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/practice/start/:id"
            element={
              <ProtectedRoute>
                <DrillExecutionPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/video/:id" 
            element={
              <ProtectedRoute>
                <Video />
              </ProtectedRoute>
            } 
          />
          {/* This is the ONLY authoritative Analysis page. Do not duplicate analysis UI elsewhere. */}
          <Route 
            path="/analysis/:analysisId" 
            element={
              <ProtectedRoute>
                {mvpNavEnabled ? (
                  <AnalysisLayout>
                    <AnalysisPage />
                  </AnalysisLayout>
                ) : (
                  <AnalysisPage />
                )}
              </ProtectedRoute>
            } 
          />
          {/* Legacy video analysis route - moved to /video-analysis/:videoId to avoid conflict with /analysis/:analysisId */}
          <Route 
            path="/video-analysis/:videoId" 
            element={
              <ProtectedRoute>
                <VideoAnalysis />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/tests/pose" 
            element={<TestPose />} 
          />
          <Route 
            path="/pose-analysis" 
            element={
              <ProtectedRoute>
                <PoseAnalysisViewer />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/pose-analysis/:analysisId" 
            element={
              <ProtectedRoute>
                <PoseAnalysisViewer />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/design/aura-preview" 
            element={
              <ProtectedRoute>
                <AuraPreview />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/swing-lab/upload" 
            element={
              <ProtectedRoute>
                <SwingLabUploadPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/swing-lab/analysis" 
            element={
              <ProtectedRoute>
                <SwingLabAnalysisPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/swing-lab/history" 
            element={
              <ProtectedRoute>
                <RunHistoryPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/swing-lab/design-mock" 
            element={
              <ProtectedRoute>
                <SwingLabDesignMockPage />
              </ProtectedRoute>
            } 
          />
          {/* Admin Routes - Protected with RBAC */}
          <Route 
            path="/admin" 
            element={
              <ProtectedAdminRoute>
                <AdminHub />
              </ProtectedAdminRoute>
            } 
          />
          <Route 
            path="/admin/health" 
            element={
              <ProtectedAdminRoute>
                <SystemHealthDashboard />
              </ProtectedAdminRoute>
            } 
          />
          <Route 
            path="/admin/ops" 
            element={
              <ProtectedAdminRoute>
                <OperationsDashboard />
              </ProtectedAdminRoute>
            } 
          />
          <Route 
            path="/admin/pipeline" 
            element={
              <ProtectedAdminRoute>
                <PipelineMonitor />
              </ProtectedAdminRoute>
            } 
          />
          <Route 
            path="/admin/coaching" 
            element={
              <ProtectedAdminRoute>
                <CoachingMonitor />
              </ProtectedAdminRoute>
            } 
          />
          <Route 
            path="/admin/analysis-quality" 
            element={
              <ProtectedAdminRoute>
                <AnalysisQualityDashboard />
              </ProtectedAdminRoute>
            } 
          />
          <Route 
            path="/admin/learning" 
            element={
              <ProtectedAdminRoute>
                <LearningAdminPage />
              </ProtectedAdminRoute>
            } 
          />
          <Route 
            path="/admin/debug" 
            element={
              <ProtectedAdminRoute>
                <DebugTools />
              </ProtectedAdminRoute>
            } 
          />
          <Route 
            path="/admin/courses/claims" 
            element={
              <ProtectedAdminRoute requiredRole="platform_admin">
                <CourseClaimsPage />
              </ProtectedAdminRoute>
            } 
          />
          <Route 
            path="/admin/badges" 
            element={
              <ProtectedAdminRoute>
                <BadgeAdminPage />
              </ProtectedAdminRoute>
            } 
          />
          <Route 
            path="/admin/users" 
            element={
              <ProtectedAdminRoute requiredRole="platform_admin">
                <UsersPage />
              </ProtectedAdminRoute>
            } 
          />
          {/* Archived - CourseAdminDashboard depends on archived useCourseDetail hook
          <Route
            path="/admin/courses/:id/*" 
            element={
              <ProtectedAdminRoute requiredRole="course_admin">
                <CourseAdminDashboard />
              </ProtectedAdminRoute>
            } 
          />
          */}
          <Route 
            path="/diagnostics" 
            element={
              <ProtectedRoute>
                <DiagnosticPage />
              </ProtectedRoute>
            } 
          />
          {/* Coach Dubs routes removed for MVP focus */}
          <Route
            path="/coach-dashboard"
            element={
              <ProtectedRoute>
                <CoachDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach/dashboard"
            element={
              <ProtectedRoute>
                <CoachDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach-directory"
            element={
              <ProtectedRoute>
                <CoachDirectoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach/:coachId/feed"
            element={
              <ProtectedRoute>
                <CoachFeedPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/golfer-dashboard"
            element={
              <ProtectedRoute>
                <GolferDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/beta/metrics"
            element={
              <ProtectedRoute>
                <BetaMetricsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/learning-stages"
            element={
              <ProtectedRoute>
                <LearningStagesAdmin />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/analysis-progress" 
            element={<ProtectedRoute><AnalysisProgressPage /></ProtectedRoute>} 
          />
          <Route 
            path="/roast" 
            element={<RoastPage />} 
          />
          {/* Demo Mode Routes (EPIC-DM01) */}
          <Route 
            path="/demo" 
            element={
              <ProtectedRoute>
                <DemoLanding />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/demo/intake/:sessionId" 
            element={
              <ProtectedRoute>
                <DemoIntake />
              </ProtectedRoute>
            } 
          />
          {/* DM01-C: Demo Capture Route */}
          <Route 
            path="/demo/capture/:sessionId" 
            element={
              <ProtectedRoute>
                <DemoCapture />
              </ProtectedRoute>
            } 
          />
          {/* DM01-C: Demo Select Route — removed, redirect stale links to capture */}
          <Route 
            path="/demo/select/:sessionId" 
            element={<DemoSelectRedirect />} 
          />
          {/* DM01-C: Demo Processing Route */}
          <Route 
            path="/demo/processing/:sessionId" 
            element={
              <ProtectedRoute>
                <DemoProcessing />
              </ProtectedRoute>
            } 
          />
          {/* DM01-D: Demo Results Route */}
          <Route 
            path="/demo/results/:sessionId" 
            element={
              <ProtectedRoute>
                <DemoResults />
              </ProtectedRoute>
            } 
          />
          {/* DM01-E: Demo Email Sent Route */}
          <Route 
            path="/demo/email-sent/:sessionId" 
            element={
              <ProtectedRoute>
                <DemoEmailSent />
              </ProtectedRoute>
            } 
          />
          {/* DM01-F: Demo Activation Route (Public - no auth required) */}
          <Route 
            path="/activate/demo" 
            element={<DemoActivation />} 
          />
          {/* DM01-G: Demo Share Routes */}
          <Route 
            path="/demo/share/:sessionId" 
            element={
              <ProtectedRoute>
                <DemoShare />
              </ProtectedRoute>
            } 
          />
          {/* DM01-G: Public Share View (no auth required) */}
          <Route 
            path="/share/:token" 
            element={<SharedSwing />} 
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <AuthBottomNav />
      </div>
    </BrowserRouter>
  );
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const mvpNavEnabled = isMvpNavEnabled();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (user) {
    // Redirect authenticated users to their home page
    const homePath = mvpNavEnabled ? "/mvp/home" : "/home";
    return <Navigate to={homePath} replace />;
  }

  return <>{children}</>;
}

function AuthBottomNav() {
  const { user } = useAuth();
  const location = window.location.pathname;
  const mvpNavEnabled = isMvpNavEnabled();
  
  // PLA-138: Always show bottom nav on training/drills pages (they're not in MVP layout)
  const isTrainingRoute = location.startsWith('/training-program') || 
                          location.startsWith('/snapshot') ||
                          location.startsWith('/practice') ||
                          location.startsWith('/drills');
  
  // Don't show old bottom nav when MVP nav is enabled (MVP has its own BottomTabs)
  // Exception: training/drills routes need the nav since they're not in MVP layout
  if (mvpNavEnabled && !isTrainingRoute) return null;
  
  return user ? <BottomNav /> : null;
}

const App = () => {
  // S-3050A: Removed non-critical Cloudflare health check to eliminate CORS errors
  // Health check can be performed manually on /admin/health page if needed
  
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <PaymentGateProvider>
            <OnboardingGate>
              <AppRoutes />
            </OnboardingGate>
          </PaymentGateProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
